import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Availability } from '@nimbus-assistant/enums/availability';
import { Activity } from '@nimbus-assistant/enums/activity';
import { TrayIcon } from '@nimbus-assistant/enums/tray-icon';
import { HomeImages } from '@nimbus-assistant/enums/home-images';
import { AppRoutes } from '@nimbus-assistant/enums/app-routes';
import { TaskState } from '@nimbus-assistant/models/task.model';
import { UserResponsibilityProfile } from '@nimbus-assistant/models/client-settings';
import { AvailabilityInfo } from '@nimbus-assistant/interfaces/availability-info';
import { UserState } from '@nimbus-library/features/user-state/models/user-state';
import { TranslationKeys } from '@nimbus-library/features/localization/models/enums/translation-keys.enum';
import { UserNotReadyReason } from '@nimbus-library/features/user-state/models/not-ready-reason';
import { AssistantHub } from './signalr/assistant.hub';
import { LoggingService } from './logging.service';
import { AuthService } from './auth.service';
import { SettingsService } from './settings.service';
import { ResponsibilityProfileService } from './responsibility-profile.service';
import { MessageService } from './message.service';
import { ServicesService } from './services.service';
import { NotAvailableReasonService } from './not-available-reason.service';
import { PresenceService } from './presence.service';
import { AssistantTasksService } from './assistant-tasks.service';

export enum UserSelectableState {
  onDutySelectable = 'onDutySelectable',
  onDutyNotSelectable = 'onDutyNotSelectable',
  rona = 'rona',
  offDuty = 'offDuty',
  licenseError = 'licenseError',
  noMsTeamsTokenError = 'noMsTeamsTokenError',
  noConnectionError = 'noConnectionError',
}

export interface IUserState {
  selectableState: UserSelectableState;
  image: string;
  message?: string;
  trayIcon: TrayIcon;
  trayIconText: TranslationKeys;
  availability: Availability;
  notReadyReason?: UserNotReadyReason;
  activity?: Activity;
  isError?: boolean;
  hasOpenNarRequest?: boolean;
}

export interface IAcwState {
  isActive: boolean;
  isExtended: boolean;
  leftInSeconds: number;
  allowToStop: boolean;
  allowToExtend: boolean;
}

export interface IRonaState {
  isActive: boolean;
  startedSecondsAgo: number;
}

interface ISelectabilityInputs {
  authenticated: boolean;
  isInRona: boolean;
  isLicensed: boolean;
  hasContactCenterLicense: boolean;
  hubIsConnected: boolean;
  availability: Availability;
  activity: Activity;
  profile: UserResponsibilityProfile;
  availabilityState: AvailabilityInfo;
  hasServiceMappings: boolean;
  openRequestsMessage: string;
  hasSettingsUpdated: boolean;
  teamsTokenReceived: boolean;
}

@UntilDestroy()
@Injectable({ providedIn: 'root' })
export class UserStateService {
  // --- Selectability ---

  private readonly allowedStates: IUserState[] = [
    {
      selectableState: UserSelectableState.onDutySelectable,
      image: HomeImages.onDutyImage,
      trayIcon: TrayIcon.Green,
      trayIconText: TranslationKeys.GeneralReachabilityReady,
      availability: Availability.Available,
    },
    {
      selectableState: UserSelectableState.onDutyNotSelectable,
      image: HomeImages.onDutyImage,
      trayIcon: TrayIcon.Red,
      trayIconText: TranslationKeys.GeneralReachabilityNotReady,
      availability: Availability.Busy,
    },
    {
      selectableState: UserSelectableState.rona,
      image: HomeImages.offDutyImage,
      trayIcon: TrayIcon.Yellow,
      trayIconText: TranslationKeys.GeneralRona,
      availability: Availability.Away,
    },
    {
      selectableState: UserSelectableState.offDuty,
      image: HomeImages.offDutyImage,
      trayIcon: TrayIcon.Disconnected,
      trayIconText: TranslationKeys.DutyOff,
      availability: Availability.Offline,
      isError: true,
    },
    {
      selectableState: UserSelectableState.licenseError,
      image: HomeImages.noLicenseImage,
      message: TranslationKeys.WarningNotLicensed,
      trayIcon: TrayIcon.Error,
      trayIconText: TranslationKeys.GeneralError,
      availability: Availability.Offline,
      isError: true,
    },
    {
      selectableState: UserSelectableState.noMsTeamsTokenError,
      image: HomeImages.noConnectionImage,
      message: TranslationKeys.WarningNoMsTeamsToken,
      trayIcon: TrayIcon.Error,
      trayIconText: TranslationKeys.GeneralError,
      availability: Availability.Offline,
      isError: true,
    },
    {
      selectableState: UserSelectableState.noConnectionError,
      image: HomeImages.noConnectionImage,
      message: TranslationKeys.WarningConnectionIssues,
      trayIcon: TrayIcon.Error,
      trayIconText: TranslationKeys.GeneralError,
      availability: Availability.Offline,
      isError: true,
    },
  ];

  private readonly defaultState = this.allowedStates.find((x) => x.selectableState === UserSelectableState.offDuty);
  private lastSetTrayIcon: TrayIcon = undefined;
  /** Matches the delay the pre-1.134 call/session-invitation screens used, so the Teams toast settles before we take focus. */
  private readonly incomingTaskBringToFrontDelayMs = 1000;

  public readonly state$ = new BehaviorSubject<IUserState>(this.defaultState);

  public readonly selectableState$ = this.state$.pipe(
    map((s) => s.selectableState),
    distinctUntilChanged(),
  );

  public readonly notReadyReason$ = this.state$.pipe(
    map((s) => s.notReadyReason),
    distinctUntilChanged(),
  );

  public readonly hasOpenNarRequest$ = this.state$.pipe(
    map((s) => s.hasOpenNarRequest ?? false),
    distinctUntilChanged(),
  );

  public readonly narMessage$ = this.state$.pipe(
    map((s) => s.message ?? ''),
    distinctUntilChanged(),
  );

  // --- ACW ---

  public readonly acwState$ = this.tasksService.acwTask$.pipe(
    map((task) => {
      if (!task) {
        return null;
      }
      const acw = task.afterCallWork;
      return {
        isActive: true,
        isExtended: task.state === TaskState.InExtendedACW,
        leftInSeconds: acw?.leftInSeconds ?? 0,
        allowToStop: acw?.allowToStop ?? false,
        allowToExtend: acw?.allowToExtend ?? false,
      } as IAcwState;
    }),
  );

  public readonly isInAcw$ = this.acwState$.pipe(map((acw) => !!acw));

  // --- RONA ---

  public readonly ronaState$ = this.tasksService.rona$.pipe(
    map((rona) => (rona ? ({ isActive: true, startedSecondsAgo: 0 } as IRonaState) : null)),
  );

  public readonly isInRona$ = this.ronaState$.pipe(map((rona) => !!rona));

  // --- Navigation ---

  public constructor(
    private readonly tasksService: AssistantTasksService,
    private readonly hub: AssistantHub,
    private readonly presenceService: PresenceService,
    private readonly authService: AuthService,
    private readonly settingsService: SettingsService,
    private readonly profileService: ResponsibilityProfileService,
    private readonly servicesService: ServicesService,
    private readonly notAvailableReasonService: NotAvailableReasonService,
    private readonly loggingService: LoggingService,
    private readonly router: Router,
  ) {
    this.setupSelectabilitySubscription();
    this.setupNavigationSubscription();
  }

  // --- ACW Actions ---

  public closeAcw(callBackend: boolean = true): void {
    this.loggingService.Info(`ACW close called (callBackend: ${callBackend})`);
    if (callBackend) {
      this.hub.closeAcw().pipe(untilDestroyed(this)).subscribe();
    }
  }

  public extendAcw(): void {
    this.loggingService.Info('ACW extend called');
    this.hub.extendAcw().pipe(untilDestroyed(this)).subscribe();
  }

  // --- RONA Actions ---

  public resetRona(): void {
    this.loggingService.Info('RONA reset by user');
    this.hub.resetRona().pipe(untilDestroyed(this)).subscribe();
  }

  // --- Selectability ---

  private setupSelectabilitySubscription(): void {
    const availabilityStateDelay = 700;

    combineLatest([
      this.authService.authenticated$.pipe(distinctUntilChanged()),
      this.isInRona$,
      this.settingsService.isLicensed$,
      this.settingsService.hasContactCenterLicense$,
      this.hub.isConnected$,
      this.presenceService.availability$.pipe(distinctUntilChanged()),
      this.presenceService.activity$.pipe(distinctUntilChanged()),
      this.profileService.profile$,
      this.hub.availabilityState$,
      this.servicesService.serviceApplicationMappings$,
      this.notAvailableReasonService.openRequestsMessage$,
      this.settingsService.settingsUpdated$,
      this.authService.teamsTokenReceived$,
    ])
      .pipe(
        debounceTime(availabilityStateDelay),
        map(
          ([
            authenticated,
            isInRona,
            isLicensed,
            hasContactCenterLicense,
            hubIsConnected,
            availability,
            activity,
            profile,
            availabilityState,
            serviceApplicationMappings,
            openRequestsMessage,
            settingsUpdated,
            teamsTokenReceived,
          ]) =>
            ({
              authenticated: authenticated as boolean,
              isInRona: isInRona as boolean,
              isLicensed: isLicensed as boolean,
              hasContactCenterLicense: hasContactCenterLicense as boolean,
              hubIsConnected: hubIsConnected as boolean,
              availability: availability as Availability,
              activity: activity as Activity,
              profile: profile as UserResponsibilityProfile,
              availabilityState: availabilityState as AvailabilityInfo,
              hasServiceMappings: !!serviceApplicationMappings,
              openRequestsMessage: (openRequestsMessage as string) ?? '',
              hasSettingsUpdated: !!settingsUpdated,
              teamsTokenReceived: teamsTokenReceived as boolean,
            }) as ISelectabilityInputs,
        ),
        filter((inputs) => this.isReadyToComputeState(inputs)),
        untilDestroyed(this),
      )
      .subscribe((inputs) => this.computeState(inputs));
  }

  private isReadyToComputeState(inputs: ISelectabilityInputs): boolean {
    if (!inputs.authenticated) {
      this.state$.next(this.defaultState);
      this.lastSetTrayIcon = undefined;
      return false;
    }
    if (!inputs.hasSettingsUpdated || !inputs.hasServiceMappings) {
      return false;
    }
    if (
      inputs.isLicensed &&
      (inputs.hubIsConnected === undefined ||
        inputs.availabilityState === undefined ||
        (inputs.hasContactCenterLicense && inputs.profile === undefined))
    ) {
      return false;
    }
    return true;
  }

  private computeState(inputs: ISelectabilityInputs): void {
    const errorState = this.getErrorState(inputs);
    let newState: IUserState;

    if (errorState) {
      MessageService.SendBringToFrontMessage();
      newState = { ...this.allowedStates.find((x) => x.selectableState === errorState) };
    } else if (inputs.isInRona) {
      newState = { ...this.allowedStates.find((x) => x.selectableState === UserSelectableState.rona) };
    } else if (inputs.profile && !inputs.profile.isDuty && inputs.availabilityState?.userStateType === UserState.OffDuty) {
      newState = { ...this.allowedStates.find((x) => x.selectableState === UserSelectableState.offDuty) };
    } else {
      const belongsToAnyTeam = this.settingsService.settings$.value?.belongsToAnyTeam;
      const teamsSelectableState =
        inputs.availabilityState?.userStateType === UserState.Selectable
          ? UserSelectableState.onDutySelectable
          : UserSelectableState.onDutyNotSelectable;

      const AvailabilityDutyMapping: Record<string, boolean> = {
        Available: true,
        AvailableIdle: true,
        Busy: true,
        BusyIdle: true,
        DoNotDisturb: true,
        Away: false,
        BeRightBack: false,
        Offline: false,
        PresenceUnknown: false,
      };

      const trayIconMapping: Record<string, TrayIcon> = {
        Available: TrayIcon.Green,
        AvailableIdle: TrayIcon.Green,
        Busy: TrayIcon.Red,
        BusyIdle: TrayIcon.Red,
        DoNotDisturb: TrayIcon.Red,
        Away: TrayIcon.Yellow,
        BeRightBack: TrayIcon.Yellow,
        Offline: TrayIcon.Disconnected,
        PresenceUnknown: TrayIcon.Disconnected,
      };

      newState = {
        selectableState: belongsToAnyTeam ? teamsSelectableState : UserSelectableState.onDutySelectable,
        image: AvailabilityDutyMapping[inputs.availability] ? HomeImages.onDutyImage : HomeImages.offDutyImage,
        trayIcon: trayIconMapping[inputs.availability] ?? TrayIcon.Disconnected,
        trayIconText: TranslationKeys.GeneralLoggedIn,
        availability: inputs.availability,
      };
    }

    newState.availability = inputs.availability;
    newState.notReadyReason = inputs.availabilityState?.notReadyReason;
    newState.activity = inputs.activity;

    if (!newState.isError) {
      newState.message = inputs.openRequestsMessage;
      newState.hasOpenNarRequest = inputs.openRequestsMessage !== '';
    }

    if (newState.trayIcon !== this.lastSetTrayIcon) {
      try {
        MessageService.SendChangeTrayIconMessage(newState.trayIcon, newState.trayIconText);
      } catch (e) {
        this.loggingService.Error(`Could not translate TrayIconMessage: ${JSON.stringify(e)}`);
      }
      this.lastSetTrayIcon = newState.trayIcon;
    }

    const oldState = this.state$.value;
    this.state$.next(newState);

    if (
      newState.selectableState !== oldState.selectableState ||
      (newState.selectableState === UserSelectableState.onDutyNotSelectable && newState.availability !== oldState.availability)
    ) {
      this.loggingService.Info(
        `Selectable state: ${newState.selectableState}, availability: ${inputs.availability}, activity: ${inputs.activity}, isInRona: ${inputs.isInRona}, hasOpenNarRequest: ${newState.hasOpenNarRequest}`,
      );
      this.notAvailableReasonService.getOpenRequests();
    }
  }

  private getErrorState(inputs: ISelectabilityInputs): UserSelectableState | null {
    if (!inputs.hasSettingsUpdated || !inputs.hasServiceMappings) {
      return UserSelectableState.noConnectionError;
    }
    if (!inputs.isLicensed) {
      return UserSelectableState.licenseError;
    }
    if (!inputs.teamsTokenReceived) {
      return UserSelectableState.noMsTeamsTokenError;
    }
    if (!inputs.hubIsConnected || (inputs.hasContactCenterLicense && !inputs.profile)) {
      return UserSelectableState.noConnectionError;
    }
    return null;
  }

  // --- Navigation ---

  private setupNavigationSubscription(): void {
    combineLatest([this.tasksService.connectedTask$, this.tasksService.acwTask$])
      .pipe(
        map(([connected, acw]) => connected ?? acw ?? null),
        distinctUntilChanged((a, b) => a?.unifiedConversationId === b?.unifiedConversationId && a?.state === b?.state),
        untilDestroyed(this),
      )
      .subscribe((task) => {
        if (task) {
          this.router.navigate([AppRoutes.call]);
          MessageService.SendBringToFrontMessage();
        } else {
          this.router.navigate([AppRoutes.home]);
        }
      });

    // Pre-1.134 the call screen and the session-invitation screen each sent this unconditionally from ngOnInit,
    // so any ringing task raised the window regardless of modality or settings. Both components were removed and
    // only the connected/ACW subscription above plus TaskToastService (audio + "Show pre-call information" only)
    // were left, so external tasks - and calls with that setting off - no longer raise the window. The host still
    // honours the user's "Bring to front" preference, so this stays unconditional here as it was before.
    this.tasksService.incomingTask$
      .pipe(
        filter((task) => !!task),
        untilDestroyed(this),
      )
      .subscribe(() => MessageService.SendBringToFrontMessage(this.incomingTaskBringToFrontDelayMs));

    this.tasksService.rona$
      .pipe(
        distinctUntilChanged((a, b) => !!a === !!b),
        filter((rona) => !!rona),
        untilDestroyed(this),
      )
      .subscribe(() => MessageService.SendBringToFrontMessage());
  }
}
